import { defineComponent as _defineComponent } from 'vue'
import type { FormModel } from "ant-design-vue"
import { message } from "ant-design-vue"
import { computed, reactive, ref, shallowRef } from "vue"

import useForm from "@/composables/useForm"
import { useServerFormErrors } from "@/composables/useServerFormErrors"
import i18n from "@/i18n"
import { translateText } from "@/modules/offers/api/productModeration"
import type {
  AttributeFormLocale,
  CreateAttributeFormValues
} from "@/modules/offers/types/createOffer"
import type { ProductModerationAttribute } from "@/modules/offers/types/offers"
import type { CreateAttributeResponse } from "@/modules/offers/types/productModeration"

import { findOrCreateAttribute } from "../../utils/findOrCreateAttribute"
import { findOrCreateAttributeValue } from "../../utils/findOrCreateAttributeValue"

type Props = {
  visible?: boolean
}
type Emits = {
  (event: "submit", payload: ProductModerationAttribute): void
  (event: "close"): void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CreateAttributeModal',
  props: {
    visible: { type: Boolean }
  },
  emits: ["submit", "close"],
  setup(__props: any, { emit }: { emit: ({
  (event: "submit", payload: ProductModerationAttribute): void
  (event: "close"): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  visible?: boolean
};




const isFetching = shallowRef(false)
const translatingField = ref<string>()

const isBusy = computed(() => isFetching.value || !!translatingField.value)

const formRef = ref<FormModel>()
const form = reactive<CreateAttributeFormValues>({
  public_name_mlang: { uk: "", ru: "" },
  value_mlang: { uk: "", ru: "" }
})

const { validateForm, isValidForm, resetFields } = useForm(formRef)
const { getFieldErrorProps, setServerErrors, clearServerError, clearServerErrors } =
  useServerFormErrors()

const formRules = computed(() => {
  const required = [
    {
      required: true,
      whitespace: true,
      message: i18n.t("offers.product_moderation_errors.required") as string
    }
  ]
  return {
    "public_name_mlang.uk": required,
    "public_name_mlang.ru": required,
    "value_mlang.uk": required,
    "value_mlang.ru": required
  }
})

const translateField = async (
  field: keyof CreateAttributeFormValues,
  locale: AttributeFormLocale
) => {
  if (isBusy.value) return

  const text = form[field][locale]

  if (!text) return

  const targetLocale = locale === "uk" ? "ru" : "uk"
  translatingField.value = `${field}.${locale}`
  try {
    const { data } = await translateText({ text, target_locale: targetLocale })
    form[field][targetLocale] = data.text.trim()
    clearServerError(`${field}.${targetLocale}`)
    formRef.value.clearValidate(`${field}.${targetLocale}`)
  } catch (error) {
    setServerErrors(error, { text: [`${field}.${locale}`] })
  } finally {
    translatingField.value = undefined
  }
}

let resolvedAttribute: CreateAttributeResponse | undefined
let resolvedAttributeName = ""

const onSubmit = async () => {
  if (isBusy.value) return

  clearServerErrors()
  isFetching.value = true
  try {
    await validateForm()

    if (!isValidForm.value) {
      message.error(i18n.t("offers.offer_form_validate_error") as string)
      return
    }

    if (!resolvedAttribute || resolvedAttributeName !== form.public_name_mlang.uk) {
      resolvedAttribute = await findOrCreateAttribute({ ...form.public_name_mlang })
      resolvedAttributeName = form.public_name_mlang.uk
    }

    const valueData = await findOrCreateAttributeValue({
      attributeCode: resolvedAttribute.code,
      attributeValueType: "STRING",
      formValues: { value_mlang: { ...form.value_mlang } }
    })

    emit("submit", { ...resolvedAttribute, value: [valueData] })
    onClose()
  } catch (error) {
    const nameFields = ["public_name_mlang.uk", "public_name_mlang.ru"]
    const valueFields = ["value_mlang.uk", "value_mlang.ru"]
    setServerErrors(error, {
      name: nameFields,
      public_name: nameFields,
      public_name_mlang: nameFields,
      value: valueFields,
      value_mlang: valueFields
    })
  } finally {
    isFetching.value = false
  }
}
const onClose = () => {
  emit("close")
  resetFields()
  clearServerErrors()
  resolvedAttribute = undefined
  resolvedAttributeName = ""
}

return { __sfc: true,props, emit, isFetching, translatingField, isBusy, formRef, form, validateForm, isValidForm, resetFields, getFieldErrorProps, setServerErrors, clearServerError, clearServerErrors, formRules, translateField, resolvedAttribute, resolvedAttributeName, onSubmit, onClose }
}

})